import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import {
  Lightning, TrendUp, TrendDown, DownloadSimple, Copy,
  TelegramLogo, WhatsappLogo, FacebookLogo, XLogo,
} from "@phosphor-icons/react";
import { API } from "../lib/api";
import { formatMoney } from "../lib/currency";
import { BrokerChip, SymbolChip } from "../components/BrokerChip";
import useSeo from "../lib/useSeo";

const PERIODS = [
  { id: "daily",   label: "Daily",   days: 1 },
  { id: "weekly",  label: "Weekly",  days: 7 },
  { id: "monthly", label: "Monthly", days: 30 },
  { id: "all",     label: "All-time", days: null },
];

// Punchy dark hero — public shared page is ALWAYS on the dark palette to
// match the exchange-share aesthetic (Delta / Binance / MT4 share cards).
// This is intentional: the viewer's OS theme is respected everywhere else
// in the app, but the shared card is a "poster" so we keep the strong look.
export default function SharedPnL() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [period, setPeriod] = useState("all");
  const [downloading, setDownloading] = useState(false);
  const heroRef = useRef(null);

  useSeo({
    title: d ? `${d.owner_name}'s Trading P&L · ${d.title} · Tradelog` : "Shared Trading P&L · Tradelog",
    description: d
      ? `${d.owner_name} shared a ${d.total_trades}-trade result with ${d.win_rate}% win rate on Tradelog. See the equity curve, best day, and daily P&L breakdown.`
      : "A shared trading P&L card on Tradelog — daily returns, win rate, best day and equity curve.",
    canonical: `/shared/${token}`,
  });

  useEffect(() => {
    // Force dark styling for the whole page — public "poster" look.
    const prev = document.documentElement.className;
    document.documentElement.classList.remove("light");
    document.documentElement.classList.add("dark");
    axios.get(`${API}/share/${token}`).then(r => setD(r.data)).catch(() => setErr("Share link not found"));
    return () => { document.documentElement.className = prev; };
  }, [token]);

  const stats = useMemo(() => {
    if (!d) return null;
    const cutoff = (() => {
      const p = PERIODS.find(p => p.id === period);
      if (!p || !p.days) return null;
      return new Date(Date.now() - p.days * 86400000).toISOString().slice(0, 10);
    })();
    const daily = (d.daily_pnl || []).filter(x => !cutoff || x.date >= cutoff);
    const total = daily.reduce((s, x) => s + (x.pnl || 0), 0);
    const winDays = daily.filter(x => x.pnl > 0).length;
    const totalDays = daily.length;
    const dayRate = totalDays ? Math.round((winDays / totalDays) * 100) : 0;
    const best = daily.reduce((b, x) => (!b || x.pnl > b.pnl ? x : b), null);
    const worst = daily.reduce((w, x) => (!w || x.pnl < w.pnl ? x : w), null);
    const roe = d.starting_balance > 0 ? (total / d.starting_balance) * 100 : null;
    return { total, dayRate, best, worst, days: totalDays, roe, daily };
  }, [d, period]);

  const positive = stats && stats.total >= 0;
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = d && stats
    ? `${positive ? "🟢" : "🔴"} ${d.title} — ${formatMoney(stats.total, d.currency || "INR", { sign: true })}${stats.roe !== null ? ` (${stats.roe >= 0 ? "+" : ""}${stats.roe.toFixed(2)}% ROE)` : ""}`
    : "";

  const shareLinks = {
    x:        `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    telegram: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + " " + shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      // sonner isn't mounted on the public page; use native feedback
      const el = document.querySelector('[data-testid="copy-link-btn"] span');
      if (el) { const t = el.textContent; el.textContent = "Copied!"; setTimeout(() => el.textContent = t, 1200); }
    } catch { /* ignore */ }
  };

  const downloadPng = async () => {
    if (!heroRef.current || downloading) return;
    setDownloading(true);
    try {
      const { default: html2canvas } = await import("html2canvas");
      // Render at 4× device-pixel-ratio for HD share-card export (crisp on
      // 2K/4K screens + Twitter/X compression). At 1200×630 CSS → 4800×2520
      // physical pixels ~ retina-clean.
      const canvas = await html2canvas(heroRef.current, {
        backgroundColor: "#0a0a0a",
        scale: Math.max(4, Math.ceil(window.devicePixelRatio || 1) * 2),
        useCORS: true,
        allowTaint: true,
        logging: false,
        imageTimeout: 0,
        // Freeze the size we render at so downloads look identical on every
        // screen size and never end up cramped on mobile.
        windowWidth: 1200,
        windowHeight: 630,
      });
      const url = canvas.toDataURL("image/png", 1.0);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(d?.title || "pnl").replace(/\W+/g, "_")}_${period}.png`;
      a.click();
    } catch (e) {
      console.error(e);
    } finally { setDownloading(false); }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-neutral-500 bg-neutral-950">{err}</div>;
  if (!d || !stats) return <div className="min-h-screen flex items-center justify-center text-neutral-500 bg-neutral-950">Loading…</div>;

  const accent = positive ? "#10b981" : "#ef4444";

  return (
    <div className="min-h-screen bg-neutral-950 text-white grain">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lightning size={22} weight="fill" className="text-primary" />
            <span className="font-display text-xl font-bold">Tradelog</span>
          </div>
          <a href="/" className="text-sm text-neutral-400 hover:text-white">Journal your trades →</a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        {/* Period selector */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="share-period-tabs">
          {PERIODS.map(p => (
            <button key={p.id} onClick={() => setPeriod(p.id)}
              data-testid={`share-period-${p.id}`}
              className={`px-4 py-2 rounded-full text-xs font-mono tracking-wider uppercase transition-all ${
                period === p.id
                  ? "bg-white text-black font-bold"
                  : "bg-white/5 text-neutral-400 hover:bg-white/10 hover:text-white"
              }`}>
              {p.label}
            </button>
          ))}
        </div>

        {/* Hero poster card — the piece users screenshot & share */}
        <div
          ref={heroRef}
          data-testid="share-hero-card"
          className="relative rounded-2xl overflow-hidden border border-white/10"
          style={{
            background:
              "radial-gradient(120% 120% at 100% 0%, rgba(16,185,129,0.10) 0%, transparent 50%), " +
              "radial-gradient(120% 120% at 0% 100%, rgba(59,130,246,0.10) 0%, transparent 50%), " +
              "linear-gradient(145deg, #0a0a0a 0%, #050505 100%)",
          }}>
          {/* Grid overlay */}
          <div className="absolute inset-0 pointer-events-none opacity-20"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), " +
                "linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
              backgroundSize: "40px 40px",
            }} />

          {/* Big arrow accent (green up / red down) — shrunk on mobile so
              the huge number never gets swallowed by the SVG. */}
          <svg
            className="absolute right-0 top-0 h-full w-2/5 sm:w-1/2 pointer-events-none opacity-70 sm:opacity-100"
            viewBox="0 0 200 400" preserveAspectRatio="xMaxYMid slice"
            aria-hidden="true">
            <defs>
              <linearGradient id="arrowGrad" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%"  stopColor={accent} stopOpacity="0.05" />
                <stop offset="100%" stopColor={accent} stopOpacity="0.9" />
              </linearGradient>
            </defs>
            {positive ? (
              <path
                d="M 40 380 L 100 300 L 100 340 L 160 240 L 160 280 L 200 200 L 200 60
                   L 155 60 L 175 40 L 130 40 L 165 5 L 200 40 L 200 60 L 200 60 Z"
                fill="url(#arrowGrad)" stroke={accent} strokeWidth="2" strokeLinejoin="round" />
            ) : (
              <path
                d="M 40 20 L 100 100 L 100 60 L 160 160 L 160 120 L 200 200 L 200 340
                   L 155 340 L 175 360 L 130 360 L 165 395 L 200 360 L 200 340 Z"
                fill="url(#arrowGrad)" stroke={accent} strokeWidth="2" strokeLinejoin="round" />
            )}
          </svg>

          <div className="relative p-6 sm:p-10 lg:p-16 min-h-[440px] sm:min-h-[520px] lg:min-h-[560px] flex flex-col justify-between gap-6 sm:gap-10 lg:gap-14">
            <div>
              {/* Brand + badges */}
              <div className="flex items-start justify-between mb-6 sm:mb-10 lg:mb-14 gap-2">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-primary/20 border border-primary/40 flex items-center justify-center shrink-0">
                    <Lightning size={18} weight="fill" className="text-primary sm:hidden" />
                    <Lightning size={22} weight="fill" className="text-primary hidden sm:block" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-display text-base sm:text-xl font-bold leading-none">Tradelog</p>
                    <p className="text-[9px] sm:text-[10px] tracking-[0.25em] sm:tracking-[0.3em] uppercase text-neutral-400 mt-1 sm:mt-1.5">Trading Journal</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                  <span data-testid="hero-direction-badge"
                    className={`px-2 py-1 sm:px-3 sm:py-1.5 rounded font-display text-[10px] sm:text-xs font-black tracking-wide ${
                      positive ? "bg-emerald-500 text-black" : "bg-red-500 text-white"}`}>
                    {positive ? "WIN" : "LOSS"}
                  </span>
                  <span className="px-2 py-1 sm:px-3 sm:py-1.5 rounded bg-white/10 text-white text-[10px] sm:text-xs font-mono font-semibold">
                    {d.currency || "INR"}
                  </span>
                </div>
              </div>

              {/* Big return */}
              <div>
                <p className={`font-display font-black leading-[0.95] tracking-tight break-words ${positive ? "text-emerald-400" : "text-red-400"} text-4xl sm:text-6xl lg:text-[92px]`}
                  data-testid="hero-total-pnl"
                  style={{ textShadow: positive ? "0 0 60px rgba(16,185,129,0.35)" : "0 0 60px rgba(239,68,68,0.35)" }}>
                  {formatMoney(stats.total, d.currency || "INR", { sign: true })}
                </p>
                {stats.roe !== null && (
                  <p className={`font-display text-xl sm:text-3xl lg:text-4xl mt-3 sm:mt-5 ${positive ? "text-emerald-400/80" : "text-red-400/80"}`}>
                    {stats.roe >= 0 ? "+" : ""}{stats.roe.toFixed(2)}% <span className="text-neutral-500 text-sm sm:text-xl">ROE</span>
                  </p>
                )}
                <p className="text-neutral-400 text-[11px] sm:text-sm mt-3 sm:mt-6 font-mono tracking-wider leading-normal whitespace-nowrap overflow-visible py-1">
                  {PERIODS.find(p => p.id === period)?.label.toUpperCase()} · {d.title}
                </p>

                {/* Broker + symbols chips — brand rail */}
                {(d.broker || (d.top_symbols || []).length > 0) && (
                  <div className="mt-4 sm:mt-6 flex flex-wrap items-center gap-1.5 sm:gap-2" data-testid="brand-rail">
                    {d.broker && <BrokerChip broker={d.broker} size="lg" />}
                    {(d.top_symbols || []).slice(0, 4).map(s => <SymbolChip key={s} symbol={s} />)}
                  </div>
                )}
              </div>
            </div>

            {/* Achievement medallion — desktop only. It overlaps mobile layout so
                we hide it below lg and let the arrow do the celebrating there. */}
            {positive && stats.total > 0 && (
              <div
                data-testid="achievement-medallion"
                className="hidden lg:block absolute top-28 right-14 pointer-events-none"
                aria-hidden="true">
                <div className="relative w-28 h-28">
                  <div className="absolute inset-0 rounded-full"
                    style={{ background: "radial-gradient(closest-side, rgba(16,185,129,0.35), transparent 70%)" }} />
                  <div className="absolute inset-2 rounded-full border-2 border-emerald-400/60"
                    style={{ boxShadow: "inset 0 4px 12px rgba(16,185,129,0.4), 0 8px 24px rgba(16,185,129,0.25)" }} />
                  <div className="absolute inset-5 rounded-full flex items-center justify-center"
                    style={{ background: "linear-gradient(145deg, #059669, #065f46)" }}>
                    <TrendUp size={28} weight="fill" className="text-emerald-200" />
                  </div>
                  {Array.from({ length: 12 }).map((_, i) => {
                    const angle = (i * 30) * Math.PI / 180;
                    const rx = 47 + Math.cos(angle) * 40;
                    const ry = 47 + Math.sin(angle) * 40;
                    return (
                      <span key={i} className="absolute w-0.5 h-1.5 bg-emerald-400/60 rounded-full"
                        style={{ left: `${rx}%`, top: `${ry}%`, transform: `translate(-50%,-50%) rotate(${i * 30}deg)` }} />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Stats row + QR — stacks on mobile, side-by-side on sm+ */}
            <div className="flex flex-col-reverse sm:flex-row sm:items-end sm:justify-between gap-4 sm:gap-8">
              <div className="grid grid-cols-3 gap-x-3 sm:gap-x-10 lg:gap-x-16 gap-y-2 min-w-0">
                <div className="min-w-0">
                  <p className="text-[9px] sm:text-[10px] tracking-[0.2em] sm:tracking-[0.25em] uppercase text-neutral-500 mb-1 sm:mb-2 leading-normal py-0.5 whitespace-nowrap">Trades</p>
                  <p className="font-mono-num font-bold text-lg sm:text-2xl leading-tight pb-0.5 whitespace-nowrap" data-testid="hero-trades">{d.total_trades}</p>
                </div>
                <div className="min-w-0">
                  <p className="text-[9px] sm:text-[10px] tracking-[0.2em] sm:tracking-[0.25em] uppercase text-neutral-500 mb-1 sm:mb-2 leading-normal py-0.5 whitespace-nowrap">Win Rate</p>
                  <p className="font-mono-num font-bold text-lg sm:text-2xl leading-tight pb-0.5 whitespace-nowrap" data-testid="hero-winrate">{d.win_rate}%</p>
                </div>
                <div className="min-w-0">
                  <p className="text-[9px] sm:text-[10px] tracking-[0.2em] sm:tracking-[0.25em] uppercase text-neutral-500 mb-1 sm:mb-2 leading-normal py-0.5 whitespace-nowrap">Best Day</p>
                  <p className={`font-mono-num font-bold text-lg sm:text-2xl leading-tight pb-0.5 whitespace-nowrap ${positive ? "text-emerald-400" : "text-red-400"}`}>
                    {stats.best ? formatMoney(stats.best.pnl, d.currency || "INR", { short: true, sign: true }) : "—"}
                  </p>
                </div>
              </div>
              <div className="self-end sm:self-auto p-2 sm:p-2.5 bg-white rounded-lg sm:rounded-xl shadow-2xl shrink-0" data-testid="hero-qr">
                <QRCodeSVG value={shareUrl} size={64} bgColor="#ffffff" fgColor="#000000" level="M" className="sm:hidden block" />
                <QRCodeSVG value={shareUrl} size={92} bgColor="#ffffff" fgColor="#000000" level="M" className="hidden sm:block" />
              </div>
            </div>
          </div>
        </div>

        {/* Share via row */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="share-via-row">
          <p className="text-xs tracking-[0.2em] uppercase text-neutral-500 mr-2">Share via</p>
          <a href={shareLinks.x} target="_blank" rel="noopener noreferrer"
            className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" data-testid="share-x-btn" title="Share on X">
            <XLogo size={16} />
          </a>
          <a href={shareLinks.telegram} target="_blank" rel="noopener noreferrer"
            className="w-10 h-10 rounded-full bg-[#229ED9]/10 hover:bg-[#229ED9]/20 flex items-center justify-center transition-colors" data-testid="share-telegram-btn" title="Share on Telegram">
            <TelegramLogo size={16} className="text-[#229ED9]" />
          </a>
          <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer"
            className="w-10 h-10 rounded-full bg-[#25D366]/10 hover:bg-[#25D366]/20 flex items-center justify-center transition-colors" data-testid="share-whatsapp-btn" title="Share on WhatsApp">
            <WhatsappLogo size={16} className="text-[#25D366]" />
          </a>
          <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer"
            className="w-10 h-10 rounded-full bg-[#1877F2]/10 hover:bg-[#1877F2]/20 flex items-center justify-center transition-colors" data-testid="share-facebook-btn" title="Share on Facebook">
            <FacebookLogo size={16} className="text-[#1877F2]" />
          </a>
          <button onClick={copyLink}
            className="h-10 px-3 rounded-full bg-white/5 hover:bg-white/10 flex items-center gap-2 transition-colors text-xs" data-testid="copy-link-btn">
            <Copy size={14} /><span>Copy link</span>
          </button>
          <button onClick={downloadPng} disabled={downloading}
            className="h-10 px-3 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground flex items-center gap-2 transition-colors text-xs disabled:opacity-50" data-testid="download-png-btn">
            <DownloadSimple size={14} />{downloading ? "Rendering…" : "Download PNG"}
          </button>
        </div>

        {/* Daily P&L bars */}
        {stats.daily.length > 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Daily P&L</p>
                <p className="font-display text-lg font-bold">
                  {stats.days} day{stats.days === 1 ? "" : "s"} · {PERIODS.find(p => p.id === period)?.label}
                </p>
              </div>
              <div className={`flex items-center gap-1 text-xs font-mono ${positive ? "text-emerald-400" : "text-red-400"}`}>
                {positive ? <TrendUp size={14} /> : <TrendDown size={14} />}
                {positive ? "Positive" : "Negative"} run
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.daily}>
                  <CartesianGrid stroke="#1a1a1a" strokeDasharray="3 3" />
                  <XAxis dataKey="date" stroke="#666" fontSize={11} />
                  <YAxis stroke="#666" fontSize={11}
                    tickFormatter={(v) => formatMoney(v, d.currency || "INR", { short: true })} />
                  <Tooltip
                    cursor={{ fill: "rgba(255,255,255,0.06)" }}
                    contentStyle={{ background: "#0e0e0e", border: "1px solid #222", borderRadius: 8, color: "#ffffff" }}
                    formatter={(v) => formatMoney(v, d.currency || "INR", { sign: true })}
                    itemStyle={{ color: "#ffffff", fontWeight: 600 }}
                    labelStyle={{ color: "#ffffff", fontWeight: 600 }} />
                  <Bar dataKey="pnl" name="P&L" radius={[3, 3, 0, 0]}>
                    {stats.daily.map((row, i) => (
                      <Cell key={`c-${i}`} fill={row.pnl >= 0 ? "#10b981" : "#ef4444"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <footer className="text-center text-xs text-neutral-600 pt-4 pb-8">
          by {d.owner_name} · shared {(d.created_at || "").slice(0, 10)} · denominated in {d.currency || "INR"}
        </footer>
      </main>
    </div>
  );
}
